import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Attributes from '..';
import Pricing from './Pricing';
import {
  VariantsChildrenType,
  VariantsType,
} from 'screens/AccessExpressV3/accessExpress.types';
import Variant, { VariantTitleBasedOnProductDesc } from './Variant';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getMobilityProductExtraAttributes } from 'screens/AccessExpressV3/utils';
import Plan from './Plan';
import Carrier from './Carrier';
import Global from './Global';
import PlanType from './PlanType';
import ServiceorEquipment from './ServiceorEquipment';
import SimType from './SimType';
import {
  DeepPartial,
  DotNotation,
  MobilityAttributesProps,
  OneLevelNestedKeys,
  SelectedAttributes,
} from './mobility.types';
import {
  findGlobal,
  findPlanTypeOption,
  findSimOption,
  getPlanTypeOptionsByState,
  getSimTypeOptionsByState,
  initialSelected,
  resetKeysToDefault,
} from './utils';
import Loading from './Loading';
import SecondaryPlan from './SecondaryPlan';

const MobilityAttributes: FC<MobilityAttributesProps> = ({
  variants,
  onAddToCart,
  productDesc,
  setSelectedIndices,
}) => {
  const { state } = useLocation();
  const selectedState = state?.selected ?? null;
  const [selected, setSelected] = useState<SelectedAttributes>(initialSelected);
  const [additionalSelected, setAdditionalSelected] =
    useState<SelectedAttributes>(initialSelected);

  const pimProductPricingId = useMemo(
    () =>
      selectedState?.choosePricingOptionId ||
      selected.choosePricingOption?.pimProductPricingId ||
      variants[0].children[0].pimProductPricingId,
    [selected, variants, selectedState],
  );

  const { data, isLoading, isFetching } = useQuery(
    ['attributes-extra', pimProductPricingId],
    () =>
      getMobilityProductExtraAttributes({
        pimProductPricingId,
      }),
    {
      refetchOnWindowFocus: false,
      enabled:
        !!pimProductPricingId &&
        productDesc !== 'BASIC CELL PHONE' &&
        selected.dataPlanNeeded.neededPlan !== 'no',
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    if (selectedState?.choosePricingOptionId && !isLoading && data) {
      let pricingOption: VariantsChildrenType | null = null;
      let variantIndex: number;
      const variantOption =
        variants.find((variant, index) => {
          pricingOption =
            variant.children.find((child) => {
              return (
                String(child.pimProductPricingId) ===
                String(selectedState?.choosePricingOptionId)
              );
            }) ?? null;
          variantIndex = index;
          return pricingOption;
        }) ?? null;

      let sim: Omit<
        SelectedAttributes,
        'chooseVariant' | 'choosePricingOption' | 'dataPlanNeeded'
      > = {} as Omit<
        SelectedAttributes,
        'chooseVariant' | 'choosePricingOption'
      >;

      let additionalSim: Omit<
        SelectedAttributes,
        'chooseVariant' | 'choosePricingOption' | 'dataPlanNeeded'
      > = {} as Omit<
        SelectedAttributes,
        'chooseVariant' | 'choosePricingOption'
      >;

      if (selectedState.simOptionId)
        sim = { ...sim, ...findSimOption(data, selectedState.simOptionId) };
      if (selectedState.planTypeOptionId)
        sim = {
          ...sim,
          ...findPlanTypeOption(data, selectedState.planTypeOptionId),
        };
      if (sim.carrier?.carrier_name === 'Granite Multi Carrier SIM')
        sim = {
          ...sim,
          global: {
            ...findGlobal(
              sim,
              selectedState.globalCoverage,
              selectedState.globalOrderCarrier,
            ),
          },
        };

      if (selectedState.additionalSimOptionId)
        additionalSim = {
          ...additionalSim,
          ...findSimOption(data, selectedState.additionalSimOptionId),
        };

      if (selectedState.additionalPlanTypeOptionId)
        additionalSim = {
          ...additionalSim,
          ...findPlanTypeOption(data, selectedState.additionalPlanTypeOptionId),
        };

      if (additionalSim.carrier?.carrier_name === 'Granite Multi Carrier SIM')
        additionalSim = {
          ...additionalSim,
          global: {
            ...findGlobal(
              additionalSim,
              selectedState.additionalGlobalCoverage,
              selectedState.additionalGlobalOrderCarrier,
            ),
          },
        };

      setSelected((prev) => ({
        ...prev,
        chooseVariant: {
          ...variantOption,
          id: String(variantIndex),
        } as unknown as VariantsType,
        choosePricingOption: pricingOption,
        dataPlanNeeded: {
          neededPlan:
            selectedState.simOptionId || selectedState.planTypeOptionId
              ? 'yes'
              : 'no',
          activation: selectedState?.dataPlanNeededActivation,
        },
        ...sim,
        secondaryPlan:
          selectedState.additionalSimOptionId &&
          selectedState.additionalPlanTypeOptionId
            ? 'yes'
            : 'no',
      }));

      setAdditionalSelected((prev) => ({
        ...prev,
        ...additionalSim,
      }));
    }
  }, [selectedState, variants, isLoading, data]);

  const _onAddToCart = () =>
    onAddToCart(selected, additionalSelected, !!selectedState);

  const showVariant = useMemo(() => {
    if (
      variants.length === 1 &&
      VariantTitleBasedOnProductDesc[productDesc ?? 'null'].length === 0
    )
      return false;
    else return true;
  }, [variants, productDesc]);

  useEffect(() => {
    if (!showVariant) {
      setSelected((prev) => ({ ...prev, chooseVariant: variants[0] }));
      setSelectedIndices((prev) => ({
        ...prev,
        variantIndex: 0,
      }));
    }
  }, [showVariant, variants, setSelectedIndices]);

  useEffect(() => {
    if (productDesc === 'SIM CARD') {
      setSelected((prev) => ({
        ...prev,
        choosePricingOption: variants[0].children[0],
      }));
      setSelectedIndices((prev) => ({
        ...prev,
        pricingOptionIndex: 0,
      }));
    }
  }, [productDesc, variants, setSelectedIndices]);

  const resetAndUpdate = (
    key: DotNotation<SelectedAttributes>,
    value: DeepPartial<SelectedAttributes>,
    setter: 'selected' | 'additional',
    keysToReset?: (
      | keyof SelectedAttributes
      | {
          parentKey: keyof SelectedAttributes;
          keys: OneLevelNestedKeys<SelectedAttributes>[];
        }
    )[],
  ) => {
    if (setter === 'selected')
      setSelected((prev) => {
        const newState = {
          ...prev,
          ...(value as SelectedAttributes),
          ...resetKeysToDefault(key, prev, value, keysToReset),
        };
        if (newState.secondaryPlan === 'no' || newState.secondaryPlan === null)
          setAdditionalSelected(initialSelected);
        return newState;
      });
    else if (setter === 'additional')
      setAdditionalSelected((prev) => {
        const newState = {
          ...prev,
          ...(value as SelectedAttributes),
          ...resetKeysToDefault(key, prev, value, keysToReset),
        };
        return newState;
      });
  };

  const _selected = useMemo(() => {
    const nonNullKeys: string[] = [];

    // Check only top-level keys
    Object.entries(selected).forEach(([key, value]) => {
      if (key === 'dataPlanNeeded') {
        // For dataPlanNeeded, check if neededPlan is not null
        if (
          value &&
          typeof value === 'object' &&
          'neededPlan' in value &&
          value.neededPlan !== null
        ) {
          nonNullKeys.push(key);
        }
      } else if (key === 'global') {
        // For global, check if scope is not null
        if (
          value &&
          typeof value === 'object' &&
          'scope' in value &&
          value.scope !== null
        ) {
          nonNullKeys.push(key);
        }
      } else if (value !== null && value !== undefined) {
        // For all other keys, simple null check
        nonNullKeys.push(key);
      }
    });
    // Check only top-level keys for additionalSelected
    Object.entries(additionalSelected).forEach(([key, value]) => {
      if (key === 'dataPlanNeeded') {
        // For dataPlanNeeded, check if neededPlan is not null
        if (
          value &&
          typeof value === 'object' &&
          'neededPlan' in value &&
          value.neededPlan !== null
        ) {
          nonNullKeys.push(`additional_${key}`);
        }
      } else if (key === 'global') {
        // For global, check if scope is not null
        if (
          value &&
          typeof value === 'object' &&
          'scope' in value &&
          value.scope !== null
        ) {
          nonNullKeys.push(`additional_${key}`);
        }
      } else if (value !== null && value !== undefined) {
        // For all other keys, simple null check
        nonNullKeys.push(`additional_${key}`);
      }
    });

    return nonNullKeys;
  }, [selected, additionalSelected]);

  const getAttributes = useCallback(
    (
      state: SelectedAttributes,
      setter: 'selected' | 'additional',
      showSecondaryPlan: boolean = true,
    ) => {
      const _keyState = setter === 'additional' ? 'additional_' : '';
      const _simTypeOptions = getSimTypeOptionsByState(state, data);
      const _planTypeOptions = getPlanTypeOptionsByState(state, data);
      return [
        ...(data?.carriers.length && state.dataPlanNeeded.neededPlan !== 'no'
          ? [
              {
                id: `${_keyState}carrier`,
                element: isFetching ? (
                  <Loading
                    title="Carrier"
                    numberOfSkeletons={data?.carriers.length}
                  />
                ) : (
                  <Carrier
                    options={data?.carriers}
                    selected={state.carrier}
                    setSelected={(carrier) => {
                      if (
                        productDesc === 'WIRELESS ROUTER' ||
                        productDesc === 'MODEM' ||
                        productDesc === 'HOTSPOT' ||
                        productDesc === 'SIM CARD'
                      ) {
                        const simType = carrier.sim_types.find(
                          (sim) => sim.sim_type === 'SIM',
                        );
                        resetAndUpdate(
                          'carrier',
                          simType
                            ? {
                                carrier,
                                simType,
                                simOption: simType.sim_options[0],
                              }
                            : { carrier },
                          setter,
                          simType
                            ? [
                                'planType',
                                'planTypeOption',
                                'global',
                                'secondaryPlan',
                              ]
                            : undefined,
                        );
                      } else resetAndUpdate('carrier', { carrier }, setter);
                    }}
                  />
                ),
              },
              ...(_simTypeOptions.length &&
              productDesc !== 'WIRELESS ROUTER' &&
              productDesc !== 'MODEM' &&
              productDesc !== 'HOTSPOT' &&
              productDesc !== 'SIM CARD'
                ? [
                    {
                      id: `${_keyState}simType`,
                      element: isFetching ? (
                        <Loading
                          title="SIM type"
                          numberOfSkeletons={_simTypeOptions.length}
                        />
                      ) : (
                        <SimType
                          options={_simTypeOptions}
                          selected={state.simType}
                          setSelected={(simType) => {
                            resetAndUpdate(
                              'simType',
                              {
                                simType,
                                simOption: simType.sim_options[0],
                              },
                              setter,
                            );
                          }}
                        />
                      ),
                    },
                  ]
                : []),
              ...(state.simType?.plan_types.length ||
              data?.carriers[0]?.sim_types[0]?.plan_types.length
                ? [
                    {
                      id: `${_keyState}planType`,
                      element: isFetching ? (
                        <Loading
                          title="Plan type"
                          size="large"
                          numberOfSkeletons={
                            data?.carriers[0]?.sim_types[0]?.plan_types.length
                          }
                        />
                      ) : (
                        <PlanType
                          options={
                            (state.simType?.plan_types ||
                              data?.carriers[0]?.sim_types[0]?.plan_types) ??
                            []
                          }
                          selected={state.planType}
                          setSelected={(planType) =>
                            resetAndUpdate('planType', { planType }, setter)
                          }
                        />
                      ),
                    },
                  ]
                : []),
              ...(state.carrier?.carrier_name === 'Granite Multi Carrier SIM'
                ? [
                    {
                      id: `${_keyState}global`,
                      element: isFetching ? (
                        <Loading title="Usage scope" />
                      ) : (
                        <Global
                          selected={state.global}
                          setScope={(scope) =>
                            resetAndUpdate(
                              'global.scope',
                              {
                                global: { scope },
                              },
                              setter,
                            )
                          }
                          setGlobalZone={(globalZone) =>
                            resetAndUpdate(
                              'global.globalZone',
                              {
                                global: { ...state.global, globalZone },
                              },
                              setter,
                            )
                          }
                          setCoverage={(coverage) =>
                            resetAndUpdate(
                              'global.coverage',
                              {
                                global: { ...state.global, coverage },
                              },
                              setter,
                            )
                          }
                          setOrderCarrier={(orderCarrier) =>
                            resetAndUpdate(
                              'global.orderCarrier',
                              {
                                global: { ...state.global, orderCarrier },
                              },
                              setter,
                            )
                          }
                        />
                      ),
                    },
                  ]
                : []),
              ...(_planTypeOptions.length
                ? [
                    {
                      id: `${_keyState}planTypeOption`,
                      element: isFetching ? (
                        <Loading
                          title="Plan"
                          numberOfSkeletons={_planTypeOptions.length}
                        />
                      ) : (
                        <Plan
                          variant={
                            state.carrier?.carrier_name === 'Granite' &&
                            (state.planType?.connection_type ===
                              'FIXED WIRELESS' ||
                              state.simType?.plan_types[0].connection_type ===
                                'FIXED WIRELESS')
                              ? 'card'
                              : 'select'
                          }
                          title="Plan"
                          options={_planTypeOptions}
                          selected={state.planTypeOption}
                          setSelected={(planTypeOption) => {
                            if (
                              state.carrier?.carrier_name === 'Granite' &&
                              state.planType?.connection_type ===
                                'FIXED WIRELESS'
                            ) {
                              const carrierSelected =
                                planTypeOption.compatible_provider?.replace(
                                  /\["([^"]+)"\]/,
                                  '$1',
                                );
                              const carrier = data.carriers.find(
                                (c) => c.carrier_name === carrierSelected,
                              );
                              const simType = carrier?.sim_types.find(
                                (s) => s.sim_type === 'SIM',
                              );

                              resetAndUpdate(
                                'planTypeOption',
                                {
                                  planTypeOption,
                                  simOption: simType?.sim_options[0],
                                },
                                setter,
                              );
                            } else
                              resetAndUpdate(
                                'planTypeOption',
                                {
                                  planTypeOption,
                                },
                                setter,
                              );
                          }}
                        />
                      ),
                    },
                  ]
                : []),
              ...(productDesc === 'WIRELESS ROUTER' && showSecondaryPlan
                ? [
                    {
                      id: `${_keyState}secondaryPlan`,
                      element: (
                        <SecondaryPlan
                          selected={state.secondaryPlan}
                          setSelected={(secondaryPlan) =>
                            resetAndUpdate(
                              'secondaryPlan',
                              { secondaryPlan },
                              setter,
                            )
                          }
                        />
                      ),
                    },
                  ]
                : []),
            ]
          : []),
      ];
    },
    [data, isFetching, productDesc],
  );

  const attributes = useMemo(
    () => getAttributes(selected, 'selected', true),
    [selected, getAttributes],
  );

  const additionalAttributes = useMemo(
    () => getAttributes(additionalSelected, 'additional', false),
    [additionalSelected, getAttributes],
  );

  if (isLoading) return null;

  return (
    <>
      <Attributes
        productDesc={productDesc}
        onAddToCart={_onAddToCart}
        selected={_selected}
        attributes={[
          ...(showVariant
            ? [
                {
                  id: 'chooseVariant',
                  element: (
                    <Variant
                      productDesc={productDesc}
                      variants={variants}
                      selected={selected.chooseVariant}
                      setSelected={(variant, index) => {
                        setSelectedIndices({
                          variantIndex: index ?? 0,
                          pricingOptionIndex: null,
                        });
                        resetAndUpdate(
                          'chooseVariant',
                          {
                            chooseVariant: variant,
                          },
                          'selected',
                        );
                      }}
                    />
                  ),
                },
              ]
            : []),
          ...(productDesc !== 'SIM CARD'
            ? [
                {
                  id: 'choosePricingOption',
                  element: (
                    <Pricing
                      variants={
                        (selected.chooseVariant?.children ||
                          variants[0]?.children) ??
                        []
                      }
                      selected={selected.choosePricingOption}
                      setSelected={(variant, index) => {
                        setSelectedIndices((prev) => ({
                          ...prev,
                          pricingOptionIndex: index ?? 0,
                        }));
                        resetAndUpdate(
                          'choosePricingOption',
                          {
                            choosePricingOption: variant,
                          },
                          'selected',
                        );
                      }}
                    />
                  ),
                },
              ]
            : []),
          ...(productDesc !== 'BASIC CELL PHONE' &&
          productDesc !== 'MODEM' &&
          productDesc !== 'WIRELESS ROUTER'
            ? [
                {
                  id: 'dataPlanNeeded',
                  element: (
                    <ServiceorEquipment
                      dataPlanNeeded={selected.dataPlanNeeded}
                      setActivation={(activation) =>
                        resetAndUpdate(
                          'dataPlanNeeded.activation',
                          {
                            dataPlanNeeded: {
                              ...selected.dataPlanNeeded,
                              activation,
                            },
                          },
                          'selected',
                        )
                      }
                      setDataPlanNeeded={(neededPlan) => {
                        resetAndUpdate(
                          'dataPlanNeeded.neededPlan',
                          {
                            dataPlanNeeded: { neededPlan },
                          },
                          'selected',
                        );
                      }}
                    />
                  ),
                },
              ]
            : []),
          ...attributes,
          ...(selected.secondaryPlan === 'yes' ? additionalAttributes : []),
        ]}
      />
    </>
  );
};

export default MobilityAttributes;
